import {
    composeRow,
    getCompany,
    getRegion,
    getSpeciality,
    getUniversityBranch,
    RowData
} from "../model/utils/utils";
import {UniversityBranches} from "../model/UniversityBranch";
import {Regions} from "../model/Region";
import {Divisions} from "../model/Division";
import {Specialities, Speciality} from "../model/Speciality";
import {Employee, Employees, Fired} from "../model/Employee";
import {composeSheetData, SheetData} from "../Excel/export";
import RowStyle = XlsxStyle.RowStyle;

export const IS_SUMMER = false;
export const CURRENT_YEAR = 2026;
export const EMPLOYEE_YEAR = IS_SUMMER ? CURRENT_YEAR : CURRENT_YEAR - 1;
export const VACANCY_YEARS = IS_SUMMER ? [CURRENT_YEAR] : [CURRENT_YEAR]
export const DEMAND_YEARS = [...Array(2036 - CURRENT_YEAR)].map((_, i) => CURRENT_YEAR + 1 + i)

export const ENTRANCE_YEARS = IS_SUMMER 
    ? [...Array(2030 - CURRENT_YEAR)].map((_, i) => CURRENT_YEAR + 1 + i) 
    : [...Array(2030 - CURRENT_YEAR)].map((_, i) => CURRENT_YEAR + 1 + i) 
export const FINANCE_YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR];
export const TRAINEE_DEMAND_YEARS = IS_SUMMER 
    ? [...Array(2030 - CURRENT_YEAR + 1)].map((_, i) => CURRENT_YEAR + i) 
    : [...Array(2030 - CURRENT_YEAR + 1)].map((_, i) => CURRENT_YEAR + i) 

    
export enum Rows {
    Title1 = 0,
    Title2,
    Company,
    Date,
    Person,
    Blank,
    Heading1,
    Heading2,
    Heading3,
}

export enum Columns {
    id = 0,
    division,
    company,
    companyRegion,
    companyCity,
    specialityCategory,
    specialityCode,
    specialityType,
    courseOfStudy,
    specialityTitle,
    specialityPriority,
    period,
    nationalProject,
    englishLevel,
    language,
    isOpk,
    employeesCurrentYear,
    employeesCurrentYearGpa,
    vacancy2025,
    vacancy2026,
    demand2027,
    demand2028,
    demand2029,
    demand2030,
    demand2031,
    demand2032,
    demand2033,
    demand2034,
    demand2035,
    demand2036,

    entranceFactorDemand,
    entranceFactorTarget,
    year,
    type,
    demand,
    position,
    gpa,
    age,
    citizenship,
    target,
    targetOpk,
    practiceType,
    gender,
    fired,
    businessType,
    employmentRegion,
    country,
    notes,
    employedBeforeGraduation,
    constructionTeam,
    hiPo,
    hiPoType,
    trained,
    practiceEducational,
    practiceUndergraduate,
    isPracticePaid,
    months,
    details,
    detailsGraduate,
    universityAbbreviation,
    universityTitle,
    universityType,
    region,
    district,
}


export const specialityColumns = [
    Columns.id,
    Columns.division,
    Columns.company,
    Columns.companyRegion,
    Columns.companyCity,
    Columns.specialityCategory,
    Columns.specialityCode,
    Columns.specialityType,
    Columns.courseOfStudy,
    Columns.specialityTitle,
    Columns.specialityPriority,
    Columns.period,
    Columns.nationalProject,
    Columns.englishLevel,
    Columns.language,
]

export const universityColumns = [
    Columns.universityAbbreviation,
    Columns.universityTitle,
    Columns.universityType,
    Columns.region,
    Columns.district,
]

export const vacancyColumns: Columns[] = [
    Columns.vacancy2025,
    Columns.vacancy2026,
]

export const employeeColumns: Columns[] = [
    Columns.employeesCurrentYear,
    Columns.employeesCurrentYearGpa,
]

export const employeeYearToColumn: {[key: number]: Columns} = {
    [EMPLOYEE_YEAR]: Columns.employeesCurrentYear,
}

export const traineeColumns: Columns[] = [
    Columns.vacancy2025,
    Columns.vacancy2026,
    Columns.demand2027,
    Columns.demand2028,
    Columns.demand2029,
    Columns.demand2030,
]

export const demandColumns: Columns[] = [
    Columns.demand2027,
    Columns.demand2028,
    Columns.demand2029,
    Columns.demand2030,
    Columns.demand2031,
    Columns.demand2032,
    Columns.demand2033,
    Columns.demand2034,
    Columns.demand2035,
    Columns.demand2036,
]

export const entranceColumns: Columns[] = [
    Columns.demand2027,
    Columns.demand2028,
    Columns.demand2029,
    Columns.demand2030,
]

export const demandColumnToYear: {[key: number]: Columns} = {
    [Columns.vacancy2025]: 2025,
    [Columns.vacancy2026]: 2026,
    [Columns.demand2027]: 2027,
    [Columns.demand2028]: 2028,
    [Columns.demand2029]: 2029,
    [Columns.demand2030]: 2030,
    [Columns.demand2031]: 2031,
    [Columns.demand2032]: 2032,
    [Columns.demand2033]: 2033,
    [Columns.demand2034]: 2034,
    [Columns.demand2035]: 2035,
    [Columns.demand2036]: 2036,
}

export const demandYearToColumn: {[key: number]: Columns} = {
    2025: Columns.vacancy2025,
    2026: Columns.vacancy2026,
    2027: Columns.demand2027,
    2028: Columns.demand2028,
    2029: Columns.demand2029,
    2030: Columns.demand2030,
    2031: Columns.demand2031,
    2032: Columns.demand2032,
    2033: Columns.demand2033,
    2034: Columns.demand2034,
    2035: Columns.demand2035,
    2036: Columns.demand2036,
}

type Merge = [number, Rows, number, Rows]

const makeMerges = (columnsOrder: Columns[], merges: [Columns, Rows, Columns, Rows][]): Merge[] => (
    merges.map(([c1, r1, c2, r2]: Merge): Merge => [columnsOrder.indexOf(c1), r1, columnsOrder.indexOf(c2), r2])
)

export const specialityMerges: Merge[] = [
    [Columns.id, Rows.Title1,  Columns.district, Rows.Title1],
    [Columns.id, Rows.Title2,  Columns.district, Rows.Title2],
    [Columns.id, Rows.Company, Columns.division, Rows.Company],
    [Columns.id, Rows.Date,    Columns.division, Rows.Date],
    [Columns.id, Rows.Person,  Columns.division, Rows.Person],

    [Columns.id,                 Rows.Heading1, Columns.id,                 Rows.Heading3],
    [Columns.division,           Rows.Heading1, Columns.division,           Rows.Heading3],
    [Columns.company,            Rows.Heading1, Columns.company,            Rows.Heading3],
    [Columns.companyRegion,      Rows.Heading1, Columns.companyRegion,      Rows.Heading3],
    [Columns.companyCity,        Rows.Heading1, Columns.companyCity,        Rows.Heading3],
    [Columns.specialityCategory, Rows.Heading1, Columns.specialityCategory, Rows.Heading3],

    [Columns.specialityCode,     Rows.Heading1, Columns.specialityPriority, Rows.Heading1],

    [Columns.specialityCode,      Rows.Heading2, Columns.specialityCode,      Rows.Heading3],
    [Columns.specialityType,      Rows.Heading2, Columns.specialityType,      Rows.Heading3],
    [Columns.courseOfStudy,       Rows.Heading2, Columns.courseOfStudy,       Rows.Heading3],
    [Columns.specialityTitle,     Rows.Heading2, Columns.specialityTitle,     Rows.Heading3],
    [Columns.specialityPriority,  Rows.Heading2, Columns.specialityPriority,  Rows.Heading3],

    [Columns.period,       Rows.Heading1, Columns.period,       Rows.Heading3],
    [Columns.nationalProject, Rows.Heading1, Columns.nationalProject, Rows.Heading3],
    [Columns.englishLevel, Rows.Heading1, Columns.englishLevel, Rows.Heading3],
    [Columns.language,     Rows.Heading1, Columns.language,     Rows.Heading3],
]

export const universityMerges: Merge[] = [
    [Columns.universityAbbreviation, Rows.Heading1, Columns.universityType, Rows.Heading2],

    [Columns.region,   Rows.Heading1, Columns.region,   Rows.Heading3],
    [Columns.district, Rows.Heading1, Columns.district, Rows.Heading3],
]

const makeWidths = (columnsOrder: Columns[]): {wch: number}[] => {
    const widths = makeEmptyRow(columnsOrder, {
        [Columns.id]: 6.5,
        [Columns.division]: 10.83,
        [Columns.company]: 24.83,
        [Columns.companyRegion]: 24.83,
        [Columns.companyCity]: 24.83,
        [Columns.specialityCategory]: 5.33,
        [Columns.specialityCode]: 14.33,
        [Columns.specialityType]: 12.67,
        [Columns.courseOfStudy]: 16.33,
        [Columns.specialityTitle]: 24,
        [Columns.specialityPriority]: 25.67,
        [Columns.period]: 12.67,
        [Columns.nationalProject]: 16.33,
        [Columns.englishLevel]: 16.33,
        [Columns.language]: 16.33,
        [Columns.employeesCurrentYear]: 7.67,
        [Columns.employeesCurrentYearGpa]: 9,
        ...vacancyColumns.reduce((r, v) => ({...r, [v]: 4.83}), {}),
        ...demandColumns.reduce((r, v) => ({...r, [v]: 4.83}), {}),
        [Columns.entranceFactorDemand]: 14.83,
        [Columns.entranceFactorTarget]: 14.83,
        [Columns.universityAbbreviation]: 17.5,
        [Columns.universityTitle]: 19,
        [Columns.universityType]: 19,
        [Columns.region]: 19,
        [Columns.district]: 18.5,
        [Columns.isOpk]: 7.83,
        [Columns.year]: 12.67,
        [Columns.type]: 12.67,
        [Columns.demand]: 12.67,
        [Columns.position]: 36.83,
        [Columns.gpa]: 12.5,
        [Columns.age]: 11.5,
        [Columns.citizenship]: 12,
        [Columns.target]: 11.5,
        [Columns.practiceType]: 24.83,
        [Columns.targetOpk]: 27.5,
        [Columns.gender]: 24,
        [Columns.fired]: 24,
        [Columns.businessType]: 7.67,
        [Columns.employmentRegion]: 19,
        [Columns.country]: 19,
        [Columns.notes]: 19,
        [Columns.employedBeforeGraduation]: 7.67,
        [Columns.constructionTeam]: 7.67,
        [Columns.hiPo]: 7.67,
        [Columns.hiPoType]: 21,
        [Columns.trained]: 21,
        [Columns.practiceEducational]: 21,
        [Columns.practiceUndergraduate]: 21,
        [Columns.details]: 27.5,
        [Columns.detailsGraduate]: 27.5,
        [Columns.isPracticePaid]: 21,
//        [Columns.months]: 21,
        [Columns.region]: 19,
    })

    return Object.keys(widths).map(column => ({wch: widths[column]}))
}


export const makeEmptyRow = (columnsOrder: Columns[], data: RowData = {}): RowData => (
    columnsOrder.reduce((r, v) => ({...r, [v]: data[v] || null}), {})
)

const makeTitleRow = (columnsOrder: Columns[], title: string = ""): RowData => makeEmptyRow(columnsOrder, {
    [Columns.id]: {s: {font: {bold: true}, alignment: {horizontal: "center", vertical: "center"}}, v: title}
})

const makeCompanyRow = (columnsOrder: Columns[], title: string): RowData => makeEmptyRow(columnsOrder, {
    [Columns.id]: {s: {font: {bold: true}, alignment: {horizontal: "right",  vertical: "top"}},    v: "Организация:"},
    [Columns.company]: {s: {font: {bold: false}, alignment: {vertical: "center"}}, v: title},
})

const makeDateRow = (columnsOrder: Columns[]): RowData => makeEmptyRow(columnsOrder, {
    [Columns.id]: {s: {font: {bold: true}, alignment: {horizontal: "right",  vertical: "center"}}, v: "Дата заполнения:"},
    [Columns.company]: {s: {font: {bold: false}, alignment: {vertical: "center"}}, v: new Date()},
})

const makePersonRow = (columnsOrder: Columns[], title: string): RowData => makeEmptyRow(columnsOrder, {
//    [Columns.id]: {s: {font: {bold: true}, alignment: {horizontal: "right",  vertical: "center"}}, v: "Ответственный:"},
    [Columns.company]: {s: {font: {bold: false}, alignment: {vertical: "center"}}, v: title},
})

export const border = {
    top:    {style: "thin", color: {auto: 1}},
    left:   {style: "thin", color: {auto: 1}},
    right:  {style: "thin", color: {auto: 1}},
    bottom: {style: "thin", color: {auto: 1}},
}

const makeHeading1Row = (columnsOrder: Columns[], data: RowData = {}): RowData => makeEmptyRow(columnsOrder, {
    ...columnsOrder.reduce((r, v) => ({...r, [v]: {s: {border}}}), {}),
    [Columns.id]:                     {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Код"},
    [Columns.division]:               {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Дивизион"},
    [Columns.company]:                {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Предприятие"},
    [Columns.companyRegion]:          {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Регион"},
    [Columns.companyCity]:            {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Город"},
    [Columns.specialityCategory]:     {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "УО"},
    [Columns.specialityCode]:         {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "bottom"}}, v: "Направление подготовки / Специальность (НП/С)"},
    [Columns.period]:                 {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Количество лет обучения"},
    [Columns.nationalProject]:        {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Потребности в рамках национального проекта «Новые атомные и энергетические технологии»"},
    [Columns.englishLevel]:           {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Уровень владения английским языком"},
    [Columns.language]:               {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Другой иностранный язык"},
    [Columns.isOpk]:                  {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Является ли ОПК"},
    [Columns.universityAbbreviation]: {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "bottom"}}, v: "Образовательная организация"},
    [Columns.region]:                 {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Регион"},
    [Columns.district]:               {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Округ"},
    ...vacancyColumns.reduce((r, v) => ({...r, [v]: {s: {border}}}), {}),
    ...demandColumns.reduce((r, v) => ({...r, [v]: {s: {border}}}), {}),
    [Columns.entranceFactorDemand]:   {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Коэффициент КЦП (общий набор)"},
    [Columns.entranceFactorTarget]:   {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Коэффициент КЦП (целевой прием)"},
    [Columns.notes]:                  {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Комментарий"},
    ...data,
})

const makeHeading2Row = (columnsOrder: Columns[], data: RowData = {}): RowData => makeEmptyRow(columnsOrder, {
    ...columnsOrder.reduce((r, v) => ({...r, [v]: {s: {border}}}), {}),
    [Columns.specialityCode]:         {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "Код ФГОС 3+/научной специальности"},
    [Columns.specialityType]:         {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "УП"},
    [Columns.courseOfStudy]:          {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Форма обучения"},
    [Columns.specialityTitle]:        {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "НП/С"},
    [Columns.specialityPriority]:     {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Тип"},
    ...data,
})

const makeHeading3Row = (columnsOrder: Columns[], data: RowData = {}): RowData => makeEmptyRow(columnsOrder, {
    ...columnsOrder.reduce((r, v) => ({...r, [v]: {s: {border}}}), {}),
    [Columns.universityAbbreviation]:  {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "Аббревиатура"},
    [Columns.universityTitle]:         {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "Название"},
    [Columns.universityType]:          {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Тип"},
    [Columns.employeesCurrentYear]:    {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: (EMPLOYEE_YEAR - 2000) + " (т)"},
    [Columns.employeesCurrentYearGpa]: {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: "средний балл"},
    ...vacancyColumns.reduce((r, v) => ({...r, [v]: {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: demandColumnToYear[v] - 2000 + ""}}), {}),
    ...demandColumns.reduce((r, v)  => ({...r, [v]: {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "left",   vertical: "center"}}, v: demandColumnToYear[v] - 2000 + ""}}), {}),
    ...data,
})

const makeFooterRow = (columnsOrder: Columns[], data: RowData[]): RowData => {
    const totalColumns = vacancyColumns.concat(demandColumns).concat([employeeColumns[0]]).map(a => `${a}`)

    const total = data.reduce<RowData>((result, row) => Object.keys(row).reduce((r, column) => ({
        ...r,
        [column]: (totalColumns.indexOf(column) !== -1) && data.length
            ? 'SUBTOTAL(9,INDIRECT("R[-' + (data.length) + ']C[0]:R[-1]C[0]",0))'
            : (r[column] || 0),
    }), result), {})
    return makeEmptyRow(columnsOrder, {
        ...columnsOrder.reduce((r: {[key: string]: number}, v) => ({
            ...r,
            [v]: total[v] && {
                s: {font: {bold: true}},
                f: total[v],
                t: "n"
            },
        }), total),
        [Columns.specialityPriority]: {s: {font: {bold: true}, alignment: {horizontal: "right"}}, v: "ИТОГО"},
    })
}

const makeNuclearFooterRow = (columnsOrder: Columns[], data: RowData[], specialities: Specialities): RowData => {
    const totalColumns = vacancyColumns.concat(demandColumns).concat([employeeColumns[0]]).map(a => `${a}`)

    const total = data
        .filter(row => {
            const code = row[columnsOrder.indexOf(Columns.specialityCode)]
            const speciality = specialities.find((speciality: Speciality) => speciality.code === code)
            return speciality && speciality.isNuclear()
        })
        .reduce<RowData>((result, row) => Object.keys(row).reduce((r, column) => ({
            ...r,
            [column]: (totalColumns.indexOf(column) !== -1)
                ? (r[column] || 0) + (row[column] || 0)
                : (r[column] || 0),
        }), result), {})
    return makeEmptyRow(columnsOrder, {
        ...columnsOrder.reduce((r: {[key: string]: number}, v) => ({
            ...r,
            [v]: total[v] && {
                s: {font: {bold: true}},
                v: total[v],
                t: "n"
            },
        }), total),
        [Columns.specialityPriority]: {s: {font: {bold: true}, alignment: {horizontal: "right"}}, v: "в т.ч. по ядерным специальностям"},
    })
}

const makeAesFooterRow = (columnsOrder: Columns[], data: RowData[]): RowData => {
    const totalColumns = vacancyColumns.concat(demandColumns).concat([employeeColumns[0]]).map(a => `${a}`)

    const total = data
        .filter(row => {
            const busynessType = row[Columns.businessType]
            return busynessType === 'Проекты по сооружению АЭС'
        })
        .reduce<RowData>((result, row) => Object.keys(row).reduce((r, column) => ({
            ...r,
            [column]: (totalColumns.indexOf(column) !== -1)
                ? (r[column] || 0) + (row[column] || 0)
                : (r[column] || 0),
        }), result), {})
    return makeEmptyRow(columnsOrder, {
        ...columnsOrder.reduce((r: {[key: string]: number}, v) => ({
            ...r,
            [v]: total[v] && {
                s: {font: {bold: true}},
                v: total[v],
                t: "n"
            },
        }), total),
        [Columns.specialityPriority]: {s: {font: {bold: true}, alignment: {horizontal: "right"}}, v: "в т.ч. по проектам сооружения АЭС"},
    })
}

const makeNationalProjectFooterRow = (columnsOrder: Columns[], data: RowData[]): RowData => {
    const totalColumns = vacancyColumns.concat(demandColumns).concat([employeeColumns[0]]).map(a => `${a}`)

    const total = data
        .filter(row => {
            const nationalProject = row[Columns.nationalProject]
            return nationalProject && nationalProject !== "Не относится к нац. проекту \"Новые атомные и энергетические технологии\""
        })
        .reduce<RowData>((result, row) => Object.keys(row).reduce((r, column) => ({
            ...r,
            [column]: (totalColumns.indexOf(column) !== -1)
                ? (r[column] || 0) + (row[column] || 0)
                : (r[column] || 0),
        }), result), {})

    return makeEmptyRow(columnsOrder, {
        ...columnsOrder.reduce((r: {[key: string]: number}, v) => ({
            ...r,
            [v]: total[v] && {
                s: {font: {bold: true}},
                v: total[v],
                t: "n"
            },
        }), total),
        [Columns.specialityPriority]: {s: {font: {bold: true}, alignment: {horizontal: "right"}}, v: "в т.ч. потребности в рамках нац. проекта \"Новые атомные и энергетические технологии\""},
    })
}

const makePreviousFooterRow = (title: string, columnsOrder: Columns[], data: { [key: string]: number }): RowData => {
    const row: RowData = {};

    const addValue = (column: Columns, value: number) => {
        if (column && columnsOrder.includes(column)) {
            row[column] = {
                s: {font: {bold: true}},
                v: (row[column] ? (row[column] as XlsxStyle.Cell).v as number : 0) + value,
                t: "n",
            }
        }
    }

    Object.entries(data).forEach(([key, value]) => {
        if (key.startsWith("vacancy")) {
            const year = Number(key.replace("vacancy", ""))
            addValue(demandYearToColumn[year], value)
        } else if (key.startsWith("employee")) {
            const year = Number(key.replace("employee", ""))
            addValue(employeeYearToColumn[year], value)
        } else if (key.startsWith("student")) {
        } else {
            const year = Number(key)
            addValue(demandYearToColumn[year], value)
        }
    })

    return makeEmptyRow(columnsOrder, {
        ...row,
        [Columns.specialityPriority]: {s: {font: {bold: true}, alignment: {horizontal: "right"}}, v: title},
    })
}

export const makeSpecialityData = ({
   id,
   courseOfStudyName,
   nationalProjectName,
   englishLevelName,
   languageName,
   divisions,
   companyId,
   specialities,
   specialityId,
}: {
    id: number | string,
    courseOfStudyName: string,
    nationalProjectName: string,
    englishLevelName: string,
    languageName: string,
    divisions: Divisions,
    companyId: number,
    specialities: Specialities,
    specialityId: number,
}): RowData => {
    const {
        division,
        company,
        region,
        city,
    } = getCompany(divisions, companyId)

    const {
        code,
        typeName,
        title,
        priority,
        category,
        period,
    } = getSpeciality(specialities, specialityId)

    return {
        [Columns.id]: id,
        [Columns.period]: period,
        [Columns.courseOfStudy]: courseOfStudyName,
        [Columns.division]: division,
        [Columns.company]: company,
        [Columns.companyRegion]: region,
        [Columns.companyCity]: city,
        [Columns.specialityCategory]: category,
        [Columns.specialityCode]: code,
        [Columns.specialityType]: typeName,
        [Columns.specialityTitle]: title,
        [Columns.specialityPriority]: priority,
        [Columns.nationalProject]: nationalProjectName,
        [Columns.englishLevel]: englishLevelName,
        [Columns.language]: languageName,
    }
}

export const makeUniversityData = ({
   regions,
   regionId,
   universityBranches,
   universityBranchId,
}: {
    regions: Regions,
    regionId: number,
    universityBranches: UniversityBranches,
    universityBranchId: number,
}): RowData => {
    const {
        name: regionName,
        district: regionDistrict,
    } = getRegion(regions, regionId)

    const {
        abbreviation: universityAbbreviation,
        title: universityTitle,
        type: universityType,
    } = getUniversityBranch(universityBranches, universityBranchId)

    return {
        [Columns.region]: regionName,
        [Columns.district]: regionDistrict,
        [Columns.universityAbbreviation]: universityAbbreviation,
        [Columns.universityTitle]: universityTitle,
        [Columns.universityType]: universityType,
    }
}

export const makeEmployeeData = ({employees}: {employees: Employees}): RowData => {
    const employeesCurrentYear = employees.filter((employee: Employee) => employee.demandYear === EMPLOYEE_YEAR && employee.fired !== Fired.Fired);

    return {
        [Columns.employeesCurrentYear]: employeesCurrentYear.size || "",
        [Columns.employeesCurrentYearGpa]: employeesCurrentYear.size
            ? employeesCurrentYear.reduce((r: number, {gpa}: Employee) => r + gpa, 0) / employeesCurrentYear.size
            : "",
    }
}

export const makeSheet = ({
    sheetTitle,
    title1,
    title2 = "",
    columnsOrder,
    company,
    fullName,
    heading1Row,
    heading2Row = {},
    data,
    merges,
    specialities,
    monitoring2019Data,
    addNuclearFooter,
    addAesProjectsFooter,
    addNationalProjectFooter,
} : {
    sheetTitle: string
    title1: string
    title2?: string
    company: string,
    fullName: string,
    columnsOrder: Columns[]
    heading1Row: RowData,
    heading2Row?: RowData,
    data: RowData[]
    merges: [Columns, Rows, Columns, Rows][]
    specialities: Specialities
    monitoring2019Data?: {
        nuclear: {[year: string]: number}
        total: {[year: string]: number}
    }
    addNuclearFooter?: boolean
    addAesProjectsFooter?: boolean
    addNationalProjectFooter?: boolean
}): SheetData => {
    console.time(sheetTitle)

    let sheetRows = composeRow(makeTitleRow(columnsOrder, title1))
        .concat(composeRow(makeTitleRow(columnsOrder, title2)))
        .concat(composeRow(makeCompanyRow(columnsOrder, company)))
        .concat(composeRow(makeDateRow(columnsOrder)))
        .concat(composeRow(makePersonRow(columnsOrder, fullName)))
        .concat(composeRow(makeEmptyRow(columnsOrder)))
        .concat(composeRow(makeHeading1Row(columnsOrder, heading1Row)))
        .concat(composeRow(makeHeading2Row(columnsOrder, heading2Row)))
        .concat(composeRow(makeHeading3Row(columnsOrder)))
        .concat(data.map(a => columnsOrder.map(b => a[b])))
        .concat(composeRow(makeFooterRow(columnsOrder, data)))

    if (addNuclearFooter) {
        sheetRows = sheetRows
            .concat(composeRow(makeNuclearFooterRow(columnsOrder, data, specialities)))
    }

    if (addAesProjectsFooter) {
        sheetRows = sheetRows
            .concat(composeRow(makeAesFooterRow(columnsOrder, data)))
    }
    if (addNationalProjectFooter) {
        sheetRows = sheetRows
            .concat(composeRow(makeNationalProjectFooterRow(columnsOrder, data)))
    }
    if (monitoring2019Data) {
        sheetRows = sheetRows
            .concat(composeRow(makePreviousFooterRow("Данные по прошлой итерации", columnsOrder, monitoring2019Data.total)))
        sheetRows = sheetRows
            .concat(composeRow(makePreviousFooterRow("в т.ч. по ядерным специальностям", columnsOrder, monitoring2019Data.nuclear)))
    }
    let sheet = composeSheetData(sheetRows)

    sheet["!merges"] = makeMerges(columnsOrder, merges).map(m => ({
        s: {c: m[0], r: m[1]},
        e: {c: m[2], r: m[3]},
    }))

    sheet["!cols"] = makeWidths(columnsOrder)

    let rows: RowStyle[] = []
    rows[Rows.Heading3] = {hpt: 32}

    sheet["!rows"] = rows

    console.timeEnd(sheetTitle)

    return {
        sheet,
        title: sheetTitle,
        filter: {
            columns: columnsOrder.length,
            row: Rows.Heading3,
        },
    }
}
